/**
 * Developer ad panel, opened by a long press on the version string in Settings.
 * It exists only in builds that serve Google TEST ads. A build made with
 * VITE_LIVE_ADS=1 has USE_TEST_ADS false, the guard below is constant false,
 * and the bundler removes this whole module's body from the output.
 */
import Phaser from 'phaser';
import { USE_TEST_ADS, USE_NATIVE_ADS } from './ads-config';
import { AD_POLICY, interstitialVerdict } from './ads-policy';
import { Progress } from './progress';
import { readEntitlement, REMOVE_ADS_UI_ENABLED } from './entitlements';
import { SANS, UI, uiButton, openModal } from './render/theme';

/** Single switch. It is the test ads flag itself, so a live build cannot have it on. */
export const DEV_MENU_ENABLED: boolean = USE_TEST_ADS;

/** Lines describing the current ad state. Pure of Phaser so it can be tested. */
export function adStateLines(now = Date.now()): string[] {
  const s = Progress.adState();
  const v = interstitialVerdict(s, { now, nextEndlessIndex: Number.MAX_SAFE_INTEGER, nextIsTutorial: false, isReplay: false });
  const e = readEntitlement();
  const secs = (t: number) => (t === 0 ? 'never' : Math.round((now - t) / 1000) + ' s ago');
  return [
    'TEST ADS BUILD',
    `native ads: ${USE_NATIVE_ADS ? 'on (device only)' : 'off'}`,
    `levels since ad: ${s.levelsSinceAd} of ${AD_POLICY.minLevelsBetween}`,
    `last interstitial: ${secs(s.lastInterstitialAt)} (gap ${AD_POLICY.minGapMs / 1000} s)`,
    `last fail: ${secs(s.lastFailAt)} (quiet ${AD_POLICY.failQuietMs / 1000} s)`,
    `endless ads start at index ${AD_POLICY.minNextEndlessIndex}`,
    `next ad now: ${v.allow ? 'allowed' : 'blocked, ' + v.reason}`,
    `remove ads: ${e.removeAds ? 'owned' : 'not owned'}, ui ${REMOVE_ADS_UI_ENABLED ? 'on' : 'off'}`,
    `${Progress.stats().adsWatched} ads watched, ${Progress.stats().livesFromAds} lives from ads`,
  ];
}

/** Open the panel. Returns false (and does nothing) in a live build. */
export function openDevMenu(scene: Phaser.Scene): boolean {
  if (!DEV_MENU_ENABLED) return false;
  const m = openModal(scene, { title: 'DEV ADS', accent: UI.accent, accentHex: UI.accentHex, h: 1000, depth: 3000 });
  m.root.add(scene.add.text(m.left, m.top, adStateLines().join('\n'), {
    fontFamily: SANS, fontSize: '28px', color: UI.text, lineSpacing: 14, wordWrap: { width: m.w - 80 },
  }));
  const close = () => m.root.destroy();
  m.root.add(uiButton(scene, m.cx, m.cy + m.h / 2 - 250, m.w - 96, 108, 'RESET AD CLOCK', UI.accent, () => {
    Progress.resetAdClock(); close();
  }));
  m.root.add(uiButton(scene, m.cx, m.cy + m.h / 2 - 110, m.w - 96, 108, 'CLOSE', UI.accent, close));
  m.dim.on('pointerup', close);
  return true;
}
